/* box-announcement-bar.js - Box the Original
   Marquee speed (px/s), rotation, pause, arrows, close. No dependencies. */
(() => {
  if (customElements.get('box-announcement-bar')) return;

  class BoxAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.desktopMq = window.matchMedia('(min-width: 750px)');
      this.reducedMq = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.group = this.querySelector('.box-ab__group');
      this.viewport = this.querySelector('.box-ab__viewport');
      this.pauseBtn = this.querySelector('.box-ab__pause');
      this.items = [];
      this.index = 0;
      this.paused = false;
      this.hovering = false;
      this.editorHold = false;

      const inEditor = !!(window.Shopify && window.Shopify.designMode);
      if (this.dataset.closable === 'true' && !inEditor) {
        try {
          if (sessionStorage.getItem(this.storageKey) === '1') {
            this.hidden = true;
            return;
          }
        } catch (e) { /* storage unavailable */ }
      }

      this.onMediaChange = () => this.setup();
      this.desktopMq.addEventListener('change', this.onMediaChange);
      this.reducedMq.addEventListener('change', this.onMediaChange);

      if (this.pauseBtn) this.pauseBtn.addEventListener('click', () => this.setPaused(!this.paused));
      const prev = this.querySelector('.box-ab__prev');
      const next = this.querySelector('.box-ab__next');
      const close = this.querySelector('.box-ab__close');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));
      if (close) close.addEventListener('click', () => this.close());

      if (this.viewport && this.dataset.pauseHover === 'true') {
        this.viewport.addEventListener('mouseenter', () => { this.hovering = true; });
        this.viewport.addEventListener('mouseleave', () => { this.hovering = this.viewport.contains(document.activeElement); });
        this.viewport.addEventListener('focusin', () => { this.hovering = true; });
        this.viewport.addEventListener('focusout', () => { this.hovering = this.viewport.matches(':hover'); });
      }

      this.onBlockSelect = (event) => {
        if (!this.contains(event.target) || this.mode !== 'rotate') return;
        const i = this.items.indexOf(event.target);
        if (i > -1) {
          this.editorHold = true;
          this.show(i);
        }
      };
      this.onBlockDeselect = () => { this.editorHold = false; };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      if ('ResizeObserver' in window && this.group) {
        this.resizeObserver = new ResizeObserver(() => this.updateDuration());
        this.resizeObserver.observe(this.group);
      }

      this.setup();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      if (this.onMediaChange) {
        this.desktopMq.removeEventListener('change', this.onMediaChange);
        this.reducedMq.removeEventListener('change', this.onMediaChange);
      }
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    get mode() {
      return this.desktopMq.matches ? this.dataset.modeDesktop : this.dataset.modeMobile;
    }

    get storageKey() {
      return `box-ab-closed:${this.dataset.sectionId}`;
    }

    get originals() {
      if (!this.group) return [];
      return Array.from(this.group.querySelectorAll(':scope > .box-ab__item:not([data-clone])'));
    }

    setup() {
      clearInterval(this.timer);
      const mode = this.mode;
      const all = this.originals;
      all.forEach((el) => el.removeAttribute('inert'));
      this.items = all.filter((el) => getComputedStyle(el).display !== 'none');

      const moving = (mode === 'marquee' && !this.reducedMq.matches) || (mode === 'rotate' && this.items.length > 1);
      if (this.pauseBtn) this.pauseBtn.hidden = !moving;

      if (mode === 'rotate') {
        const current = this.items.findIndex((el) => el.classList.contains('is-active'));
        this.show(current > -1 ? current : 0);
        this.startTimer();
      } else {
        this.updateDuration();
      }
    }

    show(i) {
      if (!this.items.length) return;
      this.index = (i + this.items.length) % this.items.length;
      const activeEl = this.items[this.index];
      this.originals.forEach((el) => {
        const active = el === activeEl;
        el.classList.toggle('is-active', active);
        el.toggleAttribute('inert', !active);
      });
    }

    step(direction) {
      this.show(this.index + direction);
      this.startTimer();
    }

    startTimer() {
      clearInterval(this.timer);
      if (this.mode !== 'rotate' || this.items.length < 2) return;
      const ms = (Number(this.dataset.interval) || 5) * 1000;
      this.timer = setInterval(() => {
        if (this.paused || this.hovering || this.editorHold || document.hidden) return;
        this.show(this.index + 1);
      }, ms);
    }

    updateDuration() {
      if (this.mode !== 'marquee' || !this.group) return;
      const width = this.group.getBoundingClientRect().width;
      const speed = Number(this.dataset.speed) || 40;
      if (width > 0) this.style.setProperty('--box-ab-duration', `${(width / speed).toFixed(2)}s`);
    }

    setPaused(paused) {
      this.paused = paused;
      this.classList.toggle('is-paused', paused);
      if (this.pauseBtn) this.pauseBtn.setAttribute('aria-pressed', String(paused));
    }

    close() {
      this.hidden = true;
      try { sessionStorage.setItem(this.storageKey, '1'); } catch (e) { /* storage unavailable */ }
    }
  }

  customElements.define('box-announcement-bar', BoxAnnouncementBar);
})();
